import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { CheckPasswordLogin } from '../../core-module/models/account';
import { AppAccessToken } from '../models/app-access-token';
import { AppResponseBase } from '../models/app-response-base';
import { Constants } from '../utils/constants';
import { JwtTokenService } from './jwt-token.service';
import { LocalStorageService } from './local-storage.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private apiUrl = `${environment.apiUrlBase}`;
  private authUrl = `${environment.authServerUrlBase}`;

  private readonly _isAuthenticated: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  readonly isAuthenticated$ = this._isAuthenticated.asObservable();  
  set isAuthenticated(val: boolean) {
    this._isAuthenticated.next(val);
  }
  get isAuthenticated() {
    return this._isAuthenticated.getValue();
  }

  // store the URL so we can redirect after logging in
  redirectUrl?: string;

  constructor(
    private http: HttpClient,
    private localStorageService: LocalStorageService,
    private jwtTokenService: JwtTokenService,
    private router: Router
  ) { }  

  acquireToken(username: string, password: string, twoFactorCode?: string, twoFactorProvider?: string, rememberDevice?: boolean): Observable<AppAccessToken> {
    const url = `${this.authUrl}/connect/token`;

    let headers = new HttpHeaders();
    headers = headers.append(Constants.InterceptorSkipHeader, '');
    headers = headers.append('Accept', 'application/json');
    headers = headers.append('Content-Type', 'application/x-www-form-urlencoded');

    let body = `grant_type=password&username=${encodeURIComponent(username)}&password=${password}&client_id=${environment.clientId}&client_secret=${environment.clientSecret}`;
    body += twoFactorCode ? `&twoFactorCode=${twoFactorCode}` : '';
    body += twoFactorProvider ? `&twoFactorProvider=${twoFactorProvider}` : '';
    body += rememberDevice ? `&rememberDevice=${rememberDevice}` : '';

    return this.http.post<AppAccessToken>(url, body, { headers });
  }

  acquireTokenByRefreshToken(refreshToken: string) {
    const url = `${this.authUrl}/connect/token`;

    let headers = new HttpHeaders();
    headers = headers.append(Constants.InterceptorSkipHeader, '');
    headers = headers.append('Accept', 'application/json');
    headers = headers.append('Content-Type', 'application/x-www-form-urlencoded');

    let body = `grant_type=refresh_token&refresh_token=${refreshToken}&client_id=${environment.clientId}&client_secret=${environment.clientSecret}`;
    return this.http.post<AppAccessToken>(url, body, { headers });
  }

  introspect(token: string): Observable<any> {
    const url = `${this.authUrl}/connect/introspect`;

    let headers = new HttpHeaders();
    headers = headers.append(Constants.InterceptorSkipHeader, '');
    headers = headers.append('Accept', 'application/json');
    headers = headers.append('Content-Type', 'application/x-www-form-urlencoded');

    let body = `token=${token}&client_id=${environment.scopeId}&client_secret=${environment.scopeSecret}`;
    return this.http.post<any>(url, body, { headers });
  }  

  confirmEmail(userId: string, token: string): Observable<any> {
    const url = `${this.apiUrl}/api/account/confirmemail`;
    let headers = new HttpHeaders();
    headers = headers.append(Constants.InterceptorSkipHeader, '');
    headers = headers.append('Content-Type', 'application/json');
    return this.http.post(url, { userId: userId, token: token }, { headers });
  }

  clearToken() {
    this.localStorageService.clear();
    this.jwtTokenService.accessToken = null;
    this.isAuthenticated = false;
  }

  clearTokenAndEndSession() {
    const idToken = this.jwtTokenService.accessToken?.id_token!;
    this.clearToken();
    this.endSession(idToken);
  }

  sendEmailResetPassword(email: string) {
    const url = `${this.apiUrl}/api/account/forgotpassword`;
    let headers = new HttpHeaders();
    headers = headers.append(Constants.InterceptorSkipHeader, '');
    headers = headers.append('Content-Type', 'application/json');
    return this.http.post(url, { email: email }, { headers });
  }

  resetPassword(userId: string, token: string, password: string, passwordConfirm: string) {
    const url = `${this.apiUrl}/api/account/resetpassword`;
    let headers = new HttpHeaders();
    headers = headers.append(Constants.InterceptorSkipHeader, '');
    headers = headers.append('Content-Type', 'application/json');
    return this.http.post(url, { userId: userId, token: token, password: password, passwordConfirm: passwordConfirm }, { headers });
  }

  checkPasswordLogin(userName: string, password: string): Observable<AppResponseBase<CheckPasswordLogin>>  {
    const url = `${this.apiUrl}/api/account/check-password-login`;
    let headers = new HttpHeaders();
    headers = headers.append(Constants.InterceptorSkipHeader, '');
    headers = headers.append('Content-Type', 'application/json');
    return this.http.post<AppResponseBase<CheckPasswordLogin>>(url, { userName: userName, password: password }, { headers });
  }

  loginAndProvision(userName: string, password: string): Observable<AppResponseBase<{ provisioned: boolean }>> {
    const url = `${this.apiUrl}/api/account/login-and-provision`;
    let headers = new HttpHeaders();
    headers = headers.append(Constants.InterceptorSkipHeader, '');
    headers = headers.append('Content-Type', 'application/json');
    return this.http.post<AppResponseBase<{ provisioned: boolean }>>(url, { userName, password }, { headers });
  }

  // When user types https://dashboard.securechoicelending.com, this method is called to start the PKCE flow.
  // Redirect user to auth server.
  initCodeAuthorizationPkce(): void {
    // Generate PKCE values
    this.generatePkceValues().then(({ codeVerifier, codeChallenge }) => {

      // Store the code verifier to use later when exchanging the authorization code
      // Use multiple storage mechanisms for reliability (localStorage may not persist before redirect)
      sessionStorage.setItem('pkce_code_verifier', codeVerifier);
      localStorage.setItem('pkce_code_verifier', codeVerifier);
      // Also store in cookie as fallback - cookies are more reliably persisted before redirects
      this.setCookie('pkce_code_verifier', codeVerifier, 10); // 10 minutes expiry

      // Build authorization request URL with PKCE
      const authUrl = new URL(`${this.authUrl}/connect/authorize`);
      authUrl.searchParams.append('client_id', `${environment.clientId}`);
      authUrl.searchParams.append('grant_type', `authorization_code`);
      authUrl.searchParams.append('response_type', 'code');
      authUrl.searchParams.append('redirect_uri', `${window.location.origin}/login-oidc`);
      authUrl.searchParams.append('scope', `${environment.scopes}`);
      authUrl.searchParams.append('code_challenge', codeChallenge);
      authUrl.searchParams.append('code_challenge_method', 'S256');
      authUrl.searchParams.append('state', this.generateRandomString(16));      

      // Redirect to authorization endpoint
      window.location.href = authUrl.toString();
    }).catch(error => {
      throw new Error(`Error generating PKCE values: ${error}`);
    });
  }

  createCodeAuthorizationUrl(): string { 

    const url = `${this.authUrl}/connect/authorize`;
    const redirectUrl = `${window.location.origin}/login-oidc`;
    const scopes = environment.scopes;

    return `${url}?client_id=${environment.clientId}&response_type=code&redirect_uri=${redirectUrl}&scope=${scopes}`;
  }

  acquireTokenByCodePkce(authorizationCode: string, rememberLogin: boolean): Observable<AppAccessToken> {

    // Retrieve the code verifier from storage (try multiple sources for reliability)
    let codeVerifier = sessionStorage.getItem('pkce_code_verifier');
    if (!codeVerifier) {
      codeVerifier = localStorage.getItem('pkce_code_verifier');
    }
    if (!codeVerifier) {
      // Fallback to cookie if localStorage/sessionStorage failed
      codeVerifier = this.getCookie('pkce_code_verifier') ?? null;
    }

    if (!codeVerifier) {
      throw new Error('pkce_code_verifier not found in storage.');
    }

    // Clear the code verifier from all storage mechanisms
    sessionStorage.removeItem('pkce_code_verifier');
    localStorage.removeItem('pkce_code_verifier');
    this.deleteCookie('pkce_code_verifier');  

    const url = `${this.authUrl}/connect/token`;
    const clientId = environment.clientId;
    const clientSecret = environment.clientSecret;
    const encodedBase64 = 'Basic ' + window.btoa(`${clientId}:${clientSecret}`);
    const redirectUrl = `${window.location.origin}/login-oidc`;

    let headers = new HttpHeaders();
    headers = headers.append(Constants.InterceptorSkipHeader, '');
    headers = headers.append('Accept', 'application/json');
    headers = headers.append('Content-Type', 'application/x-www-form-urlencoded');
    headers = headers.append('Authorization', encodedBase64);

    // Build token request
    const tokenRequest: any = {
      client_id: `${clientId}`,
      grant_type: 'authorization_code',
      code: authorizationCode,
      redirect_uri: `${redirectUrl}`,
      code_verifier: codeVerifier,
      rememberLogin: rememberLogin
    };

    //let body = `grant_type=authorization_code&code_verifier=${codeVerifier}&code=${code}&client_id=${environment.clientId}&redirect_uri=${redirectUrl}&rememberLogin=${rememberLogin}`;
    //return this.http.post<AppAccessToken>(url, body, { headers });
    return this.http.post<AppAccessToken>(url, new URLSearchParams(tokenRequest), { headers });
  }

  acquireTokenByCode(code: string, rememberLogin: boolean): Observable<AppAccessToken> {
    const url = `${this.authUrl}/connect/token`;
    const clientId = environment.clientId;
    const clientSecret = environment.clientSecret;
    const encodedBase64 = 'Basic ' + window.btoa(`${clientId}:${clientSecret}`);
    const redirectUrl = `${window.location.origin}/login-oidc`;

    let headers = new HttpHeaders();
    headers = headers.append(Constants.InterceptorSkipHeader, '');
    headers = headers.append('Accept', 'application/json');
    headers = headers.append('Content-Type', 'application/x-www-form-urlencoded');
    headers = headers.append('Authorization', encodedBase64);

    let body = `grant_type=authorization_code&code=${code}&client_id=${clientId}&redirect_uri=${redirectUrl}&rememberLogin=${rememberLogin}`;
    return this.http.post<AppAccessToken>(url, body, { headers });
  }

  //createLogoutUrl(idToken: string): string {
  //  const url = `${this.apiUrl}/connect/endsession`;
  //  const callbackUrl = `${environment.clientAppUrlBase}/logout-callback-oidc`;
  //  return `${url}?id_token_hint=${idToken}&post_logout_redirect_uri=${callbackUrl}`;
  //}

  endSession(idToken: string) {
    const authUrl = `${this.authUrl}/connect/endsession`;
    const callbackUrl = `${window.location.origin}`;
    window.location.href = `${authUrl}?id_token_hint=${idToken}&post_logout_redirect_uri=${callbackUrl}`;
  }

  getCookie(name: string): string | undefined {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      try {
        return decodeURIComponent(parts.pop()?.split(';').shift() || '');
      } catch (e) {
        return undefined;
      }
    }
    return undefined;
  }

  setCookie(name: string, value: string, minutes: number): void {
    const expires = new Date();
    expires.setTime(expires.getTime() + minutes * 60 * 1000);
    document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
  }

  private deleteCookie(name: string): void {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
  }

  // Generate a random code verifier
  private generateRandomString = (length: number): string => {
    const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    let text = '';
    const randomValues = new Uint8Array(length);
    window.crypto.getRandomValues(randomValues);

    for (let i = 0; i < length; i++) {
      text += possible.charAt(randomValues[i] % possible.length);
    }

    return text;
  };

  private generatePkceValues(): Promise<{ codeVerifier: string; codeChallenge: string }> {
   
    // Generate the code verifier (between 43 and 128 characters)
    const codeVerifier = this.generateRandomString(64);

    // Create the code challenge using SHA-256
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);

    return window.crypto.subtle.digest('SHA-256', data).then(digest => {
      // Convert to base64 URL format
      // 1. Convert the array buffer to a regular array
      const digestArray = Array.from(new Uint8Array(digest));
      // 2. Convert to base64
      const base64Digest = btoa(String.fromCharCode.apply(null, digestArray));
      // 3. Convert to base64URL by replacing characters that are invalid in URLs
      const codeChallenge = base64Digest
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');

      return { codeVerifier, codeChallenge };
    });
  }

}
